import React from 'react';
import { motion } from 'framer-motion';
import BlueHeaderSection from '../BlueHeader';

// Assets
import devopsImg1 from "../../asset/images/cloud-migration1.jpg";
import devopsImg2 from "../../asset/images/cloud-migration1.png";
import devopsImg3 from "../../asset/images/cloud-migration2.png";
import devopsImg4 from "../../asset/images/cloud-migration3.png";

// Shared Animation Constant
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.4, ease: 'easeOut' } 
  }
};

const services = [
  {
    id: "dev-svc-1",
    title: 'DevOps Assessment & Strategy',
    text: 'We evaluate your current delivery pipeline, infrastructure, and team workflows, then define a DevOps roadmap aligned with your release goals and compliance needs.',
  },
  {
    id: "dev-svc-2",
    title: 'CI/CD Pipeline Implementation',
    text: 'We design and implement automated build, test, and deployment pipelines using tools like GitHub Actions, GitLab CI, Jenkins, or Azure DevOps.',
  },
  {
    id: "dev-svc-3",
    title: 'Infrastructure as Code & Automation',
    text: 'Using Terraform, CloudFormation, Ansible, or similar tools, we codify your infrastructure to enable repeatable, error-free provisioning and scaling.',
  },
  {
    id: "dev-svc-4",
    title: 'Cloud & Containerization',
    text: 'We help you adopt Docker and Kubernetes on AWS, Azure, or GCP, enabling scalable, resilient, cloud-native architectures.',
  },
  {
    id: "dev-svc-5",
    title: 'Monitoring, Logging & Reliability',
    text: 'We integrate observability tooling (Prometheus, Grafana, ELK, CloudWatch, etc.) so you can detect issues early and maintain high uptime.',
  },
  {
    id: "dev-svc-6",
    title: 'DevSecOps & Compliance',
    text: 'Security is built into your pipelines with automated checks, secrets management, and policy controls rather than added at the end.',
  }
];

const features = [
  {
    id: "dev-feat-1",
    title: "Certified DevOps Engineers",
    description: "Our team includes certified experts with hands-on experience across major clouds and DevOps toolchains.",
    image: devopsImg1
  },
  {
    id: "dev-feat-2",
    title: "Faster Time to Market",
    description: "Automated pipelines, shorter release cycles, and fewer failures help you ship features faster with confidence.",
    image: devopsImg2
  },
  {
    id: "dev-feat-3",
    title: "Security Integrated by Design",
    description: "We embed security checks into your CI/CD, code, and infrastructure so compliance is continuous, not a last-minute hurdle.",
    image: devopsImg3
  },
  {
    id: "dev-feat-4",
    title: "Data-Driven Monitoring",
    description: "We set up metrics, logs, and alerts so decisions are driven by real-time system health and performance data.",
    image: devopsImg4
  },
];

const DevopsConsulting = () => {
  return (
    <div className="overflow-hidden">
      <BlueHeaderSection heading="DEVOPS CONSULTING" url="DevOps Consulting" showServices={true} />

      {/* Content Section */}
      <section className="py-12 md:py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">

          {/* Main Heading */}
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Accelerate Delivery. Automate Everything. Operate at Scale.
            </h2>
            <div className="w-24 h-1 bg-[#f7941d] mx-auto"></div>
          </div>

          {/* Intro */}
          <div className="mb-16 text-center">
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
              At **BytesFarms Technologies**, we help businesses embrace DevOps to bridge the gap between development and operations for faster deployments and high-performing systems.
            </p>
          </div>

          {/* Services List - Returning to Side-Border Design */}
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((item) => (
              <motion.div
                key={item.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="p-6 rounded-2xl bg-white shadow-md border-l-4 border-[#f7941d] hover:shadow-lg transition-all duration-300"
              >
                <h4 className="text-xl font-semibold text-[#f7941d] mb-3">{item.title}</h4>
                <p className="text-gray-800 leading-relaxed tracking-tight">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section - Fixed Render Logic */}
      <section className="py-12 md:py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Why choose BytesFarms for DevOps Consulting?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {features.map((feature) => (
              <motion.div
                key={feature.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="group relative overflow-hidden rounded-2xl shadow-2xl h-[220px] md:h-[250px] bg-gray-900"
              >
                <img
                  src={feature.image}
                  alt="" // Fixed: Empty alt prevents text duplication with the H3 below
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-opacity duration-300"></div>
                
                <div className="relative z-10 p-6 md:p-8 h-full flex flex-col justify-center text-center">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DevopsConsulting;